var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useContext, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import styled, { css } from 'styled-components';
import { variant } from 'styled-system';
import { Elevation } from '../../essentials';
import { theme } from '../../essentials/theme';
import { get } from '../../utils/themeGet';
import { getSemanticValue } from '../../utils/cssVariables';
export const ANIMATION_DURATION = 140;
const TRANSITION_KEY = 'banner-animation';
const fallDown = css `
    top: 0;

    &.${TRANSITION_KEY}-appear, &.${TRANSITION_KEY}-enter {
        opacity: 0;
        transform: translateY(-100%);
    }

    &.${TRANSITION_KEY}-appear-active, &.${TRANSITION_KEY}-enter-active {
        opacity: 1;
        transform: translateY(0);
    }

    &.${TRANSITION_KEY}-exit {
        opacity: 1;
        transform: translateY(0);
    }

    &.${TRANSITION_KEY}-exit-done, &.${TRANSITION_KEY}-exit-active {
        opacity: 0;
        transform: translateY(-100%);
    }
`;
const riseUp = css `
    bottom: 0;

    &.${TRANSITION_KEY}-appear, &.${TRANSITION_KEY}-enter {
        opacity: 0;
        transform: translateY(100%);
    }

    &.${TRANSITION_KEY}-appear-active, &.${TRANSITION_KEY}-enter-active {
        opacity: 1;
        transform: translateY(0);
    }

    &.${TRANSITION_KEY}-exit {
        opacity: 1;
        transform: translateY(0);
    }

    &.${TRANSITION_KEY}-exit-done, &.${TRANSITION_KEY}-exit-active {
        opacity: 0;
        transform: translateY(100%);
    }
`;
const bannerVariants = variant({
    variants: {
        info: {
            backgroundColor: getSemanticValue('background-surface-neutral-emphasized'),
            color: getSemanticValue('foreground-on-background-neutral')
        },
        success: {
            backgroundColor: getSemanticValue('background-surface-success-emphasized'),
            color: getSemanticValue('foreground-on-background-success')
        },
        danger: {
            backgroundColor: getSemanticValue('background-surface-danger-emphasized'),
            color: getSemanticValue('foreground-on-background-danger')
        }
    }
});
const getBannerPosition = (props) => {
    if (props.position) {
        return props.position === 'top' ? fallDown : riseUp;
    }
    return props.variant === 'danger' ? fallDown : riseUp;
};
const AnimatedBanner = styled.div.attrs({ theme }).withConfig({ displayName: "AnimatedBanner", componentId: "sc-1k9bw58" }) `
    overflow: auto;
    box-sizing: border-box;
    padding: ${get('space.3')};
    background-color: ${getSemanticValue('background-surface-neutral-emphasized')};
    color: ${getSemanticValue('foreground-on-background-info')};

    position: fixed;
    left: 0;
    width: 100%;
    z-index: ${Elevation.BANNER};

    transition: transform ${ANIMATION_DURATION}ms ease-out, opacity ${ANIMATION_DURATION * 0.75}ms ease;

    ${props => getBannerPosition(props)}

    ${bannerVariants}
`;
const DismissContext = React.createContext(() => { });
const useBannerDismiss = () => {
    const dismiss = useContext(DismissContext);
    if (dismiss === undefined) {
        throw new Error('useBannerDismiss must be used within a <Banner />');
    }
    return dismiss;
};
const Banner = (_a) => {
    var { children, onClose } = _a, rest = __rest(_a, ["children", "onClose"]);
    const [visible, setVisible] = useState(true);
    const dismissFunction = () => {
        setVisible(false);
        if (onClose) {
            setTimeout(() => onClose(), ANIMATION_DURATION);
        }
    };
    const renderChildren = () => {
        if (typeof children === 'function') {
            return children(dismissFunction);
        }
        return children;
    };
    return (React.createElement(CSSTransition, { in: visible, classNames: TRANSITION_KEY, timeout: ANIMATION_DURATION, unmountOnExit: true, appear: true },
        React.createElement(AnimatedBanner, Object.assign({}, rest),
            React.createElement(DismissContext.Provider, { value: dismissFunction }, renderChildren()))));
};
export { Banner, useBannerDismiss };
